import { Link, type LinkProps } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { type EventLibrary } from '#app/types/analytics/eventLibrary.js';
import { ChevronLeftIcon } from '@contra/icons/icons';
import {
  type ComponentPropsWithoutRef,
  type Ref,
  useCallback,
  useId,
} from 'react';
import { css, styled } from 'styled-components';

type BackToProfileProps = ComponentPropsWithoutRef<'div'> & {
  readonly analyticsProperties?: Pick<
    EventLibrary['link'],
    'is_owner' | 'portfolio_project_id' | 'service_id'
  >;
  readonly className?: string;
  readonly elementRef?: Ref<HTMLAnchorElement>;
  readonly onClick?: () => void;
  readonly to: LinkProps['to'];
  readonly variant?: 'large' | 'small';
};

const BackToProfileContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    color: ${theme.colorsV2.gray70};
  `}
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  span {
    margin-left: 6px;
    font-weight: 600;
  }
`;

const Divider = styled.div`
  width: 1px;
  margin: 0 8px;
  height: 24px;
  border-left: 1px solid ${({ theme }) => theme.colorsV2.gray30};
`;

const ArrowIcon = styled(ChevronLeftIcon)`
  height: 20px;
  width: 20px;
`;

export const BackToProfile = ({
  analyticsProperties,
  children = 'Back to Profile',
  className,
  elementRef,
  onClick,
  to,
  variant = 'small',
}: BackToProfileProps) => {
  const { track } = useAnalytics();
  const handleClick = useCallback(() => {
    if (analyticsProperties) {
      track('link', {
        action: 'tapped',
        destination_path: typeof to === 'string' ? to.toString() : '',
        destination_route: 'user',
        link_name: 'back',
        ...analyticsProperties,
      });
    }

    if (onClick) {
      onClick();
    }
  }, [analyticsProperties, onClick, to, track]);
  const id = useId();
  return (
    <BackToProfileContainer className={className}>
      <Link
        onClick={handleClick}
        ref={elementRef}
        to={to}
      >
        <Container>
          <ArrowIcon aria-labelledby={id} />{' '}
          {variant === 'large' ? <Divider /> : null}
          <Text
            as="span"
            id={id}
            textStyle={variant === 'large' ? 'fifthLevelHeadline' : 'bodySmall'}
          >
            {children}
          </Text>
        </Container>
      </Link>
    </BackToProfileContainer>
  );
};
